import React, {
    useCallback,
    useEffect,
    useMemo,
  } from 'react';
  import {
    useHistory,
    useParams,
  } from 'react-router';
  import { useDispatch, useSelector } from 'react-redux';
  import { useTranslation } from 'react-i18next';
  import debounce from 'lodash/debounce';
  import { isEmpty } from 'lodash';
  import {
    changeToConsultMode,
    changeToCreateMode,
    changeToUpdateMode,
    failure,
    fetchByIdAsync,
    loading,
    resetModel,
    selectGrupoDespesasReceita,
    selectMode,
    setError,
    setModel,
    setResponse,
    success,
    addToList,
    updateOnList,
    changeModeTo,
  } from '../../../../../features/grupo-despesa-receita/grupoDespesaReceitaSlice';
  import {
    isCreateMode,
    isConsultMode,
    isUpdateMode,
    isNoneMode,
  } from '../../../../../utils/store/store-utils';
  import { register, save } from '../../../../../features/grupo-despesa-receita/grupoDespesaReceitaAPI';
  import QCXRegistrationFormPageTemplate from '../../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate';
  import useAutoChangeMode from '../../../../../utils/hooks/mode/useAutoChangeMode';
  import QCXDespesaReceitaForm from '../../../../../components/despesa-receita/QCXDespesaReceitaForm';
  import { getTipoModeloLabelByValue } from '../../../../../shared-components/select-tipo-modelo/QCXFinalSelectTipoModeloAutocomplete';
  import { getStringOrUndefined, parseToBoolean } from '../../../../../utils/general/parse-utils';
import QCXGrupoDespesaReceitaForm from '../../../../../components/grupo-despesa-receita/QCXGrupoDespesaReceitaForm';
  
export default function GrupoDespesaReceitaRegistrationPage({ authInfo = {} }) {
  
  const { t } = useTranslation();

  const { id } = useParams();

  const history = useHistory();

  const dispatch = useDispatch();

  const mode = useSelector(selectMode);

  const grupoDespesasReceita = useSelector(selectGrupoDespesasReceita);

  const isNone = useMemo(() => (
    isNoneMode(mode)
  ), [mode]);

  const isCreate = useMemo(() => (
    isCreateMode(mode)
  ), [mode]);

  const isConsult = useMemo(() => (
    isConsultMode(mode)
  ), [mode]);

  const isUpdate = useMemo(() => (
    isUpdateMode(mode)
  ), [mode]);

  const fetchById = useCallback((despesaReceitaId) => (
    dispatch(fetchByIdAsync(despesaReceitaId))
  ), []);

  const handleChangeToCreate = () => {
    dispatch(changeToCreateMode());
  };

  const handleChangeToConsult = () => {
    dispatch(changeToConsultMode());
  };

  const handleChangeToUpdate = () => {
    dispatch(changeToUpdateMode());
  };

  useAutoChangeMode((currentMode) => (
    dispatch(changeModeTo(currentMode))
  ), [isUpdate]);

  useEffect(() => {
    if (!isEmpty(id)) {
      dispatch(fetchByIdAsync(id));
      handleChangeToConsult();
    }
  }, [id]);

  const handleCancelUpdate = () => {
    if (id) {
      fetchById(id);
    }
    handleChangeToConsult();
  };

  const normalize = useCallback((normalizedData) => {

    const unnormalizedData = {
        id: normalizedData?.id ?? null,
        code: normalizedData?.code ?? '',
        description: getStringOrUndefined(normalizedData?.description) ?? '',
        active : normalizedData?.active ?? true,
        insertionDate : normalizedData?.insertionDate ?? null,
      };

    return unnormalizedData;
  }, [
    getStringOrUndefined,
    parseToBoolean,
  ]);

  const unnormalize = useCallback((normalizedData) => {
    
    const unnormalizedData = {
      id: normalizedData?.id ?? null,
      code: normalizedData?.code ?? '',
      description: normalizedData?.description ?? '',
      active : normalizedData?.active ?? true,
      insertionDate : normalizedData?.insertionDate ?? new Date(),
    };

    return unnormalizedData;
  }, [
    getStringOrUndefined,
    parseToBoolean,
  ]);

  const handleDispatchSetModel = useCallback((data) => {

    const normalizedData = normalize(data);

    dispatch(
      setModel(normalizedData)
    );
  }, [normalize]);

  const create = async (data) => {
    const executeDebounced = debounce(async () => {
      const tipoModelo = getTipoModeloLabelByValue(data?.tipo);

      try {
        const response = await register(data);

        if (response?.status === 201) {
          dispatch(resetModel());

          const created = response?.data;

          handleDispatchSetModel(created);
          dispatch(addToList({ data: created }));

          const handleResultWithDebounce = debounce(() => {
            history.push(t('com.muralis.qcx.url.moduloAgenciamentoTarifa'));

            dispatch(success());
            dispatch(setResponse({
              status: response.status,
              data: created,
              message: t('com.muralis.qcx.mensagem.despesaReceitaCadastrada', { tipoModelo, descricao: data?.description }),
            }));
          }, 500);

          handleResultWithDebounce();
        }
      } catch (error) {
        const defaultErrorMessage = t('com.muralis.qcx.erro.erroCadastrarDespesaReceita', { tipoModelo, descricao: data?.description });
        const errorMessage = error?.response?.data?.message
          ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
          : `${defaultErrorMessage}.`;

        dispatch(failure());
        dispatch(setError({
          message: errorMessage,
        }));
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const update = async (data) => {
    const executeDebounced = debounce(async () => {
      const tipoModelo = getTipoModeloLabelByValue(data?.tipo);

      try {
        const response = await save(data);

        if (response?.status === 200) {
          const handleResultWithDebounce = debounce(() => {
            handleChangeToConsult();
            dispatch(success());

            const saved = response?.data;

            dispatch(setResponse({
              status: response.status,
              data: saved,
              message: t('com.muralis.qcx.mensagem.despesaReceitaSalva', { tipoModelo, descricao: saved?.description }),
            }));

            dispatch(setModel(saved));
            dispatch(updateOnList({ data: saved }));
            dispatch(fetchByIdAsync(saved?.id));
          }, 500);

          handleResultWithDebounce();
          handleChangeToConsult();
        }
      } catch (error) {
        const defaultErrorMessage = t('com.muralis.qcx.erro.erroSalvarDadosDespesaReceita', { tipoModelo, descricao: data?.description });
        const errorMessage = error?.response?.data?.message
          ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
          : `${defaultErrorMessage}.`;

        dispatch(failure());
        dispatch(setError({
          message: errorMessage,
        }));
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const handleSubmit = async (data) => {
    const normalizedData = normalize(data);

    if (isUpdate) {
      await update(normalizedData);
    } else {
      await create(normalizedData);
    }
  };

  const model = useMemo(() => (
    !isCreate
      ? unnormalize(grupoDespesasReceita)
      : {}
  ), [
    isCreate,
    grupoDespesasReceita,
    unnormalize,
  ]);

  const actionName = useMemo(() => {
    if (isCreate || isNone) {
      return t('com.muralis.qcx.acoes.nova');
    }
    if (isConsult) {
      return t('com.muralis.qcx.acoes.visualizar');
    }
    return t('com.muralis.qcx.acoes.alterar');
  }, [isNone, isCreate, isConsult]);

  const breadcrumbs = useMemo(() => ([
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.cadastros'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloCadastrosFinanceiros'),
        name: t('com.muralis.qcx.financeiro.labelPlural'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloAgenciamentoTarifa'),
        name: t('com.muralis.qcx.despesa.grupoDespesasReceitas'),
      },
    },
    {
      text: {
        name: actionName,
      },
    },
  ]), [actionName]);

  const pageTitle = useMemo(() => {
    const tipo = getTipoModeloLabelByValue(
      grupoDespesasReceita?.tipo
    ) || t('com.muralis.qcx.despesa.grupoDespesaReceita');

    return (
      isNone || isCreate
        ? t('com.muralis.qcx.despesa.novaGrupoDespesaReceita')
        : t('com.muralis.qcx.despesa.grupoDespesaReceitaExistente', { tipo, ID: grupoDespesasReceita?.description || grupoDespesasReceita?.id || '-' })
    );
  }, [
    isNone,
    isCreate,
    grupoDespesasReceita,
    getTipoModeloLabelByValue,
  ]);

  return (
    <QCXRegistrationFormPageTemplate
      pageTitle={pageTitle}
      breadcrumbs={breadcrumbs}
      isCreate={isCreate}
      isConsult={isConsult}
      isUpdate={isUpdate}
      handleChangeToCreate={handleChangeToCreate}
      handleChangeToConsult={handleChangeToConsult}
      handleChangeToUpdate={handleChangeToUpdate}
      handleCancelUpdate={handleCancelUpdate}
      authInfo={authInfo}
    >
      {(formProps) => (
        <QCXGrupoDespesaReceitaForm
            isCreate={isCreate}
            initialValues={model}
            handleSubmit={handleSubmit}
            handleChangeModel={handleDispatchSetModel}
            authInfo={authInfo}
            requiredRoles={['grupo-despesa-receita']}
            {...formProps}
        />
      )}
    </QCXRegistrationFormPageTemplate>
  );
}